import { useEffect, useRef, useState } from "react";
import {
  Instagram,
  Youtube,
  Mail,
  Gamepad2,
  Play,
  Zap,
  Monitor,
  Trophy,
  ArrowUpRight,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_abd323b2-33fe-4f89-8f3a-86a9beec8214/artifacts/fddfs3uf_azure.retro.game.png";

// Placeholder / dummy social links – user will update later
const SOCIALS = {
  youtube: "https://youtube.com/@blueemulator",
  instagram: "https://www.instagram.com/blue.emulator",
  email: "blue.emulator14@gmail.com",
};

const NAV_ITEMS = [
  { label: "Home", target: "home", id: "nav-home-link" },
  { label: "About", target: "about", id: "nav-about-link" },
  { label: "Channels", target: "channels", id: "nav-channels-link" },
  { label: "Contact", target: "contact", id: "nav-contact-link" },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

const Nav = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <nav
      className="fixed top-0 inset-x-0 z-[100] backdrop-blur-md bg-[#050510]/85 border-b-4 border-[#2f2fef]"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("home")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo-button"
        >
          <img
            src={LOGO_URL}
            alt="Blue Emulator logo"
            className="w-10 h-10 sm:w-12 sm:h-12 pixelated border-2 border-[#2f2fef] group-hover:border-[#39ff14] transition-colors"
          />
          <span className="font-pixel text-[10px] sm:text-xs text-white group-hover:text-[#39ff14] transition-colors">
            BLUE&nbsp;EMULATOR
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.target}
              onClick={() => scrollTo(item.target)}
              className="font-pixel text-[10px] tracking-widest text-white/80 hover:text-[#39ff14] transition-colors"
              data-testid={item.id}
            >
              {item.label}
            </button>
          ))}
        </div>

        <a
          href={SOCIALS.youtube}
          target="_blank"
          rel="noreferrer"
          className="hidden sm:inline-flex pixel-btn !py-2 !px-4 !text-[9px]"
          data-testid="nav-cta-subscribe"
        >
          <Youtube className="w-4 h-4" /> Subscribe
        </a>
      </div>
    </nav>
  );
};

const Hero = () => {
  const revealRef = useReveal();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      className="relative pt-28 sm:pt-36 pb-24 sm:pb-32 pixel-grid-bg overflow-hidden"
      data-testid="hero-section"
    >
      {/* Neon blobs */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#2f2fef] opacity-30 blur-[120px]" />
      <div className="pointer-events-none absolute top-20 right-0 w-[420px] h-[420px] rounded-full bg-[#39ff14] opacity-[0.12] blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <div ref={revealRef} className="reveal lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 px-3 py-2 border-2 border-[#39ff14] text-[#39ff14] font-pixel text-[9px] tracking-widest mb-8"
            data-testid="hero-badge"
          >
            <Zap className="w-3.5 h-3.5" />
            NOW PLAYING · RETRO MODE
          </div>

          <h1
            className="font-pixel text-3xl sm:text-5xl lg:text-6xl leading-[1.15] text-white mb-6"
            data-testid="hero-title"
          >
            BLUE
            <br />
            <span className="text-[#39ff14] text-glow-green flicker">
              EMULATOR
            </span>
            <br />
            <span className="text-[#4a4aff] text-glow-blue">— GAMING</span>
          </h1>

          <p
            className="font-mono-retro text-xl sm:text-2xl text-white/80 max-w-xl mb-4"
            data-testid="hero-subtitle"
          >
            Retro runs. Classic consoles. Fresh uploads every week.
          </p>
          <p
            className="text-sm sm:text-base text-[#a0a0b5] max-w-xl mb-10"
            data-testid="hero-description"
          >
            A content creator pixel-hopping through the games that built a
            generation — NES, SNES, Game Boy, and beyond. And yes, the new
            stuff too, when the mood strikes.
          </p>

          <div className="flex flex-wrap gap-5">
            <a
              href={SOCIALS.youtube}
              target="_blank"
              rel="noreferrer"
              className="pixel-btn"
              data-testid="hero-youtube-cta"
            >
              <Play className="w-4 h-4" /> Watch on YouTube
            </a>
            <a
              href={SOCIALS.instagram}
              target="_blank"
              rel="noreferrer"
              className="pixel-btn pixel-btn-blue"
              data-testid="hero-instagram-cta"
            >
              <Instagram className="w-4 h-4" /> Follow on IG
            </a>
            <button
              onClick={() => scrollTo("about")}
              className="font-pixel text-[10px] tracking-widest text-white/70 hover:text-[#39ff14] transition-colors underline underline-offset-8 decoration-dotted"
              data-testid="hero-scroll-down"
            >
              ↓ SCROLL
            </button>
          </div>

          <div className="mt-12 flex items-center gap-6 font-mono-retro text-[#a0a0b5] text-lg">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#39ff14] animate-pulse" /> ONLINE
            </span>
            <span>PRESS&nbsp;START<span className="blink" /></span>
          </div>
        </div>

        {/* Right: logo display */}
        <div className="lg:col-span-5">
          <div
            className="relative mx-auto max-w-sm"
            data-testid="hero-logo-frame"
          >
            {/* CRT frame */}
            <div className="relative border-4 border-[#2f2fef] bg-[#0a0a20] p-6 shadow-[12px_12px_0_rgba(47,47,239,0.55)]">
              <div className="absolute -top-3 left-4 right-4 flex items-center justify-between">
                <div className="flex gap-1.5">
                  <span className="block w-3 h-3 bg-[#ff3b3b]" />
                  <span className="block w-3 h-3 bg-[#39ff14]" />
                  <span className="block w-3 h-3 bg-[#4a4aff]" />
                </div>
                <span className="font-pixel text-[8px] text-[#39ff14] bg-[#0a0a20] px-2">
                  CH-01
                </span>
              </div>

              <div className="relative aspect-square bg-[#2f2fef] overflow-hidden">
                <img
                  src={LOGO_URL}
                  alt="Blue Emulator Gaming Logo"
                  className="w-full h-full object-contain pixelated"
                />
                {/* Scanlines inside tv */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.25) 3px)",
                  }}
                />
              </div>

              <div className="mt-4 flex items-center justify-between font-pixel text-[9px] text-white/70">
                <span>◀ CH</span>
                <span className="text-[#39ff14]">BLUE-EMU · RETRO</span>
                <span>CH ▶</span>
              </div>
            </div>

            {/* Floating tag */}
            <div className="absolute -bottom-6 -left-6 bg-[#39ff14] text-[#050510] font-pixel text-[9px] px-3 py-2 border-4 border-[#050510] shadow-[4px_4px_0_#2f2fef] rotate-[-3deg]">
              2D · 8-BIT · 16-BIT
            </div>
            <div className="absolute -top-6 -right-6 bg-[#050510] text-[#39ff14] font-pixel text-[9px] px-3 py-2 border-4 border-[#39ff14] rotate-[4deg]">
              PLAYER 1
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const revealRef = useReveal();
  return (
    <section
      id="about"
      className="relative py-24 sm:py-32"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={revealRef} className="reveal grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="font-pixel text-[10px] tracking-widest text-[#39ff14]">
              // ABOUT
            </span>
            <h2 className="font-pixel text-2xl sm:text-3xl lg:text-4xl text-white mt-4 leading-tight" data-testid="about-title">
              WHO&apos;S
              <br />
              BEHIND THE
              <br />
              <span className="text-[#4a4aff] text-glow-blue">CONTROLLER?</span>
            </h2>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <p className="font-mono-retro text-2xl text-white/90" data-testid="about-lead">
              I&apos;m a content creator who treats old cartridges like fine wine
              and new releases like Friday-night takeout — both on the menu.
            </p>
            <p className="text-base sm:text-lg text-[#a0a0b5] leading-relaxed">
              Right now the focus is retro: speedruns, deep dives, and
              nostalgia-packed playthroughs of the NES, SNES, Game Boy, Genesis
              and arcade classics that shaped gaming. I cover modern titles
              too — but the pixels always call me back.
            </p>
            <p className="text-base sm:text-lg text-[#a0a0b5] leading-relaxed">
              Videos drop on YouTube. Snappy clips and BTS on Instagram. Press
              start — let&apos;s play.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 pt-4">
              {[
                { icon: Gamepad2, label: "Consoles", value: "NES → PS5" },
                { icon: Monitor, label: "Eras", value: "8-BIT · HD" },
                { icon: Trophy, label: "Vibe", value: "Chill · Fun" },
              ].map(({ icon: Icon, label, value }) => (
                <div
                  key={label}
                  className="pixel-card p-4"
                  data-testid={`about-stat-${label.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="pixel-icon-box">
                      <Icon className="w-4 h-4 text-[#39ff14]" />
                    </div>
                    <span className="font-pixel text-[9px] tracking-widest text-white/60">
                      {label}
                    </span>
                  </div>
                  <p className="font-pixel text-sm text-[#39ff14]">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ChannelCard = ({
  platform,
  handle,
  description,
  href,
  Icon,
  accent,
  testId,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="pixel-card p-8 block group relative overflow-hidden"
    data-testid={testId}
  >
    <div
      className="absolute -right-10 -top-10 w-40 h-40 opacity-10 group-hover:opacity-20 transition-opacity"
      style={{ color: accent }}
    >
      <Icon className="w-full h-full" />
    </div>

    <div className="flex items-center justify-between mb-6 relative">
      <div className="pixel-icon-box" style={{ borderColor: accent }}>
        <Icon className="w-6 h-6" style={{ color: accent }} />
      </div>
      <ArrowUpRight
        className="w-6 h-6 text-white/50 group-hover:text-[#39ff14] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
      />
    </div>

    <h3
      className="font-pixel text-base sm:text-lg text-white mb-3"
      style={{ textShadow: `0 0 14px ${accent}66` }}
    >
      {platform}
    </h3>
    <p className="font-mono-retro text-xl text-[#39ff14] mb-3">{handle}</p>
    <p className="text-sm text-[#a0a0b5] leading-relaxed">{description}</p>

    <div className="mt-8 flex items-center gap-2 font-pixel text-[9px] tracking-widest text-white/70 group-hover:text-[#39ff14] transition-colors">
      VISIT CHANNEL <span>→</span>
    </div>
  </a>
);

const Channels = () => {
  const revealRef = useReveal();
  return (
    <section
      id="channels"
      className="relative py-24 sm:py-32 border-t-4 border-b-4 border-[#2f2fef] bg-[#070718]"
      data-testid="channels-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={revealRef} className="reveal">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div>
              <span className="font-pixel text-[10px] tracking-widest text-[#39ff14]">
                // CHANNELS
              </span>
              <h2
                className="font-pixel text-2xl sm:text-3xl lg:text-4xl text-white mt-4 leading-tight"
                data-testid="channels-title"
              >
                WHERE TO
                <br />
                <span className="text-[#39ff14] text-glow-green">
                  PLAY ALONG
                </span>
              </h2>
            </div>
            <hr className="pixel-hr flex-1 hidden sm:block" />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <ChannelCard
              platform="YOUTUBE"
              handle="@blueemulator"
              description="Full playthroughs, retro deep-dives, console reviews, and the occasional speedrun attempt. New video every week."
              href={SOCIALS.youtube}
              Icon={Youtube}
              accent="#ff3b3b"
              testId="channel-youtube-card"
            />
            <ChannelCard
              platform="INSTAGRAM"
              handle="@blue.emulator"
              description="Clips, moments, behind-the-scenes, setup shots and reels. The quick-hit side of the channel."
              href={SOCIALS.instagram}
              Icon={Instagram}
              accent="#39ff14"
              testId="channel-instagram-card"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const revealRef = useReveal();
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SOCIALS.email);
      toast.success("Email copied to clipboard", {
        description: SOCIALS.email,
      });
    } catch {
      toast.error("Couldn't copy email — try opening the mail link instead.");
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 sm:py-32"
      data-testid="contact-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={revealRef}
          className="reveal pixel-card p-10 sm:p-14 text-center"
          data-testid="contact-card"
        >
          <span className="font-pixel text-[10px] tracking-widest text-[#39ff14]">
            // CONTACT
          </span>
          <h2
            className="font-pixel text-2xl sm:text-3xl lg:text-4xl text-white mt-5 leading-tight"
            data-testid="contact-title"
          >
            COLLAB, SPONSOR,
            <br />
            <span className="text-[#4a4aff] text-glow-blue">OR JUST SAY GG.</span>
          </h2>
          <p className="font-mono-retro text-xl text-white/80 mt-6 max-w-xl mx-auto">
            Got a game you want me to play? A brand match? Fan mail?
            Drop a line — I actually read them.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
            <a
              href={`mailto:${SOCIALS.email}`}
              className="pixel-btn"
              data-testid="contact-email-btn"
            >
              <Mail className="w-4 h-4" /> {SOCIALS.email}
            </a>
            <button
              onClick={copyEmail}
              className="pixel-btn pixel-btn-blue"
              data-testid="contact-copy-btn"
            >
              Copy Email
            </button>
          </div>

          <div className="mt-10 flex items-center justify-center gap-5 text-white/70">
            <a
              href={SOCIALS.youtube}
              target="_blank"
              rel="noreferrer"
              className="pixel-icon-box"
              aria-label="YouTube"
              data-testid="contact-social-youtube"
            >
              <Youtube className="w-5 h-5 text-[#ff3b3b]" />
            </a>
            <a
              href={SOCIALS.instagram}
              target="_blank"
              rel="noreferrer"
              className="pixel-icon-box"
              aria-label="Instagram"
              data-testid="contact-social-instagram"
            >
              <Instagram className="w-5 h-5 text-[#39ff14]" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer
    className="border-t-4 border-[#2f2fef] bg-[#050510] py-10"
    data-testid="site-footer"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
      <div className="flex items-center gap-3">
        <img
          src={LOGO_URL}
          alt="Blue Emulator"
          className="w-8 h-8 pixelated border-2 border-[#2f2fef]"
        />
        <span className="font-pixel text-[9px] text-white/70">
          BLUEEMULATOR.COM
        </span>
      </div>
      <p className="font-mono-retro text-sm text-[#a0a0b5]">
        © {new Date().getFullYear()} Blue Emulator — Gaming. All rights reserved.
      </p>
      <p className="font-pixel text-[9px] text-[#39ff14]">
        GAME&nbsp;OVER<span className="blink" />
      </p>
    </div>
  </footer>
);

const BootOverlay = () => {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 1600);
    return () => clearTimeout(t);
  }, []);
  if (!visible) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#050510] flex items-center justify-center"
      data-testid="boot-overlay"
    >
      <div className="text-center">
        <img
          src={LOGO_URL}
          alt="Blue Emulator"
          className="w-24 h-24 mx-auto pixelated border-4 border-[#2f2fef] shadow-[6px_6px_0_#39ff14]"
        />
        <p className="font-pixel text-xs text-[#39ff14] mt-6 text-glow-green">
          LOADING<span className="blink" />
        </p>
      </div>
    </div>
  );
};

export default function Landing() {
  useEffect(() => {
    document.title = "Blue Emulator – Gaming";
  }, []);

  return (
    <div className="relative min-h-screen bg-[#050510] text-white" data-testid="landing-page">
      <BootOverlay />
      <div className="crt-overlay" aria-hidden="true" />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0a0a20",
            border: "3px solid #2f2fef",
            borderRadius: 0,
            color: "#ffffff",
            fontFamily: "Space Mono, monospace",
          },
        }}
      />
      <Nav />
      <main>
        <Hero />
        <About />
        <Channels />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
